import React from "react";
import { Link } from "react-router-dom";
import {
  Smartphone, Globe, Database, Workflow, Palette, Rocket, Wrench, Search,
  ArrowRight, CheckCircle2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { IMAGES } from "../mock";

const offerings = [
  { icon: Smartphone, title: "Mobile App Development", desc: "Native-quality iOS and Android apps that put your business in your customers' pockets — from booking apps to loyalty programs." },
  { icon: Globe, title: "Web Application Development", desc: "Fast, modern web apps and customer portals — online booking systems, dashboards, e-commerce and more." },
  { icon: Database, title: "Custom Business Systems", desc: "Purpose-built software for your operations: scheduling, inventory, CRM, reporting and internal tools tailored to how you work." },
  { icon: Workflow, title: "Integrations & Automation", desc: "Connect the tools you already use — payments, email, accounting and government portals — and automate the busywork." },
];

const process = [
  { icon: Search, n: "01", title: "Discover", desc: "We sit down with you to understand your business, your customers and the problem worth solving." },
  { icon: Palette, n: "02", title: "Design", desc: "We map out the user experience and design clean, modern interfaces you can react to before a line of code is written." },
  { icon: Wrench, n: "03", title: "Build", desc: "We develop in short cycles with regular demos, so you see progress every step of the way." },
  { icon: Rocket, n: "04", title: "Launch & Support", desc: "We deploy, train your team and stay on board with ongoing support and improvements." },
];

const reasons = [
  "Local Cayman Islands team — meet us face to face in George Town",
  "Proven product experience — we built and run Blue Ledger, our own accounting platform",
  "Fixed, transparent quotes with no surprise billing",
  "Modern technology stack: React, mobile, cloud and AI integrations",
  "Ongoing maintenance and support plans after launch",
];

export default function AppDev() {
  return (
    <div data-testid="appdev-page">
      {/* Hero */}
      <section className="bg-gradient-to-b from-[#F2FAFB] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <Badge className="bg-[#E8F7F9] text-[#0C8695] hover:bg-[#E8F7F9] border border-[#B5E6EC] font-semibold px-4 py-1.5 rounded-full mb-6">
              New Service • App Development
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-extrabold text-[#0A2540] tracking-tight leading-[1.1]">
              Custom Apps Built in the <span className="text-[#0E9AAA]">Cayman Islands</span>, for Cayman Businesses
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              From mobile apps to online booking systems and internal business tools, our development team designs, builds and supports software that helps island businesses work smarter and serve customers better.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/contact">
                <Button size="lg" className="bg-[#0E9AAA] hover:bg-[#0C8695] text-white font-bold rounded-lg px-8 h-12 shadow-lg hover:shadow-xl transition-shadow">
                  Start a Project <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
              <Link to="/blue-ledger">
                <Button size="lg" variant="outline" className="border-[#0A2540] text-[#0A2540] hover:bg-[#0A2540] hover:text-white font-bold rounded-lg px-8 h-12 transition-colors">
                  See Our Work: Blue Ledger
                </Button>
              </Link>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -bottom-6 -left-6 w-40 h-40 bg-[#0E9AAA]/10 rounded-full blur-2xl" />
            <img src={IMAGES.appDev} alt="App development design" className="rounded-2xl shadow-2xl w-full h-[400px] object-cover" />
          </div>
        </div>
      </section>

      {/* Offerings */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-[#0E9AAA] font-bold text-sm uppercase tracking-widest mb-3">What We Build</div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0A2540] tracking-tight">Software That Works as Hard as You Do</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-7">
            {offerings.map((o) => (
              <Card key={o.title} className="rounded-2xl border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <CardContent className="p-8">
                  <div className="w-13 h-13 w-12 h-12 rounded-xl bg-[#E8F7F9] flex items-center justify-center mb-5">
                    <o.icon className="text-[#0E9AAA]" size={24} />
                  </div>
                  <h3 className="font-extrabold text-[#0A2540] text-lg">{o.title}</h3>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">{o.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-[#F6FAFB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-[#0E9AAA] font-bold text-sm uppercase tracking-widest mb-3">How We Work</div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0A2540] tracking-tight">A Clear Process From Idea to Launch</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-7">
            {process.map((p) => (
              <div key={p.n} className="bg-white rounded-2xl p-7 border border-slate-100 shadow-sm hover:shadow-lg transition-shadow">
                <div className="flex items-center justify-between mb-5">
                  <div className="w-12 h-12 rounded-xl bg-[#0A2540] flex items-center justify-center">
                    <p.icon className="text-[#39C1D0]" size={22} />
                  </div>
                  <span className="text-3xl font-extrabold text-slate-200">{p.n}</span>
                </div>
                <h3 className="font-bold text-[#0A2540] text-lg">{p.title}</h3>
                <p className="mt-2.5 text-sm text-slate-600 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why us */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-14 items-center">
          <img src={IMAGES.devTeam} alt="Development team collaborating" className="rounded-2xl shadow-xl w-full h-96 object-cover" />
          <div>
            <div className="text-[#0E9AAA] font-bold text-sm uppercase tracking-widest mb-3">Why MBS</div>
            <h2 className="text-3xl font-extrabold text-[#0A2540] tracking-tight">A Partner, Not Just a Vendor</h2>
            <ul className="mt-7 space-y-4">
              {reasons.map((r) => (
                <li key={r} className="flex items-start gap-3 text-slate-700">
                  <CheckCircle2 size={20} className="text-[#0E9AAA] mt-0.5 shrink-0" />
                  <span className="leading-relaxed">{r}</span>
                </li>
              ))}
            </ul>
            <Link to="/contact" className="inline-block mt-8">
              <Button size="lg" className="bg-[#0A2540] hover:bg-[#123B63] text-white font-bold rounded-lg px-8 transition-colors">
                Tell Us About Your Project <ArrowRight size={17} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
